import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Download, Heart, ArrowLeft, Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";

interface PurchasedGuide {
  id: string;
  name: string;
  file_path: string | null;
}

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  useSEO({
    title: "Payment Successful | Global Conflict Monitor",
    description: "Your Global Conflict Monitor purchase is confirmed. Download your guides and access your new premium intelligence tools.",
    path: "/payment-success",
    noindex: true,
  });
  const isPurchase = searchParams.get("purchase") === "success";
  const isDonation = searchParams.get("donation") === "success";
  const sessionId = searchParams.get("session_id");
  const { user, checkSubscription } = useAuth();
  const { toast } = useToast();
  const [guides, setGuides] = useState<PurchasedGuide[]>([]);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Signup form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupDone, setSignupDone] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);

  // Pre-fill email and get Stripe customer ID from session for guest users
  useEffect(() => {
    if (user || !sessionId) return;
    const fetchSessionInfo = async () => {
      try {
        const { data } = await supabase.functions.invoke("get-session-email", {
          body: { sessionId },
        });
        if (data?.email) setEmail(data.email);
        if (data?.customer_id) setStripeCustomerId(data.customer_id);
      } catch { /* silent */ }
    };
    fetchSessionInfo();
  }, [user, sessionId]);

  useEffect(() => {
    if (!user || !isPurchase || !sessionId) return;

    const verifyAndFetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fnError } = await supabase.functions.invoke("verify-payment", {
          body: { sessionId },
        });
        if (fnError) throw fnError;

        if (data?.verified) {
          setVerified(true);
          setGuides(data.guides || []);
        } else {
          setError(data?.reason || "Payment could not be verified. Please contact support.");
        }
      } catch (err: any) {
        setError(err.message || "Verification failed");
      } finally {
        setLoading(false);
      }
    };

    verifyAndFetch();
  }, [user, isPurchase, sessionId]);

  // Refresh subscription status and link Stripe customer when user lands here
  useEffect(() => {
    if (!user || !isPurchase || !sessionId) return;
    
    // Link Stripe customer ID to profile
    const linkCustomer = async () => {
      try {
        const { data } = await supabase.functions.invoke("get-session-email", {
          body: { sessionId },
        });
        if (data?.customer_id) {
          await supabase
            .from("profiles")
            .update({ stripe_customer_id: data.customer_id })
            .eq("user_id", user.id);
        }
      } catch { /* silent */ }
    };
    linkCustomer();
    
    // Force immediate subscription check
    checkSubscription();
    // Check again after a short delay to allow processing
    const timer = setTimeout(() => checkSubscription(), 3000);
    return () => clearTimeout(timer);
  }, [user, isPurchase, sessionId, checkSubscription]);

  const handleDownload = async (filePath: string) => {
    const { data, error } = await supabase.storage
      .from("guides")
      .createSignedUrl(filePath, 300);
    if (error || !data?.signedUrl) return;
    window.open(data.signedUrl, "_blank");
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSignupLoading(true);
    try {
      const { data: signupData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: stripeCustomerId ? { stripe_customer_id: stripeCustomerId } : undefined,
        },
      });
      if (error) throw error;
      
      // If auto-confirmed (or already confirmed), link customer immediately
      if (signupData.user && stripeCustomerId) {
        await supabase
          .from("profiles")
          .update({ stripe_customer_id: stripeCustomerId })
          .eq("user_id", signupData.user.id);
      }
      setSignupDone(true);
      toast({
        title: "Account created!",
        description: "Check your email to confirm, then sign in to activate your Premium subscription.",
      });
    } catch (err: any) {
      toast({ title: "Signup error", description: err.message, variant: "destructive" });
    } finally {
      setSignupLoading(false);
    }
  };

  if (!isPurchase && !isDonation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="max-w-md border-border bg-card">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No payment information found.</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg"
      >
        <Card className="border-border bg-card">
          <CardContent className="space-y-6 p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              {isDonation ? (
                <Heart className="h-8 w-8 text-primary" />
              ) : (
                <CheckCircle className="h-8 w-8 text-primary" />
              )}
            </div>

            <div>
              <h1 className="mb-2 text-2xl font-bold text-foreground">
                {isDonation ? "Thank You for Your Donation!" : "Payment Successful!"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isDonation
                  ? "Your generous contribution will help those affected by conflict. A receipt has been sent to your email."
                  : user
                    ? "Your Premium subscription is now active. Enjoy full access!"
                    : "Create your account below to activate your Premium subscription."}
              </p>
            </div>

            {/* Signup form for guest users who just paid */}
            {isPurchase && !user && !signupDone && (
              <form onSubmit={handleSignup} className="space-y-4 text-left">
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold text-foreground">Create your account</h2>
                  </div>
                  <p className="mb-4 text-xs text-muted-foreground">
                    Enter your email and a password to link your Premium subscription to your account.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="email" className="text-xs">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="password" className="text-xs">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Min 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        minLength={6}
                        required
                      />
                    </div>
                  </div>
                </div>
                <Button type="submit" className="w-full gap-2" disabled={signupLoading}>
                  {signupLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Creating account...</>
                  ) : (
                    <><UserPlus className="h-4 w-4" /> Create Account & Activate Premium</>
                  )}
                </Button>
              </form>
            )}

            {/* Signup success message */}
            {isPurchase && !user && signupDone && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <CheckCircle className="mx-auto mb-2 h-6 w-6 text-primary" />
                <p className="text-sm font-medium text-foreground">Account created!</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Check your email to confirm your account, then sign in to access your Premium features.
                </p>
                <Button asChild variant="outline" size="sm" className="mt-3">
                  <Link to="/auth">Sign In</Link>
                </Button>
              </div>
            )}

            {/* Guide downloads for logged-in users with product purchases */}
            {isPurchase && user && sessionId && (
              <div className="space-y-3 text-left">
                <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Your Guides
                </h2>
                {loading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying your payment…
                  </div>
                ) : error ? (
                  <p className="text-sm text-destructive">{error}</p>
                ) : verified && guides.length > 0 ? (
                  guides.map((g) => (
                    <div
                      key={g.id}
                      className="flex items-center justify-between rounded-lg border border-border bg-secondary p-3"
                    >
                      <span className="text-sm font-medium text-foreground">{g.name}</span>
                      {g.file_path ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(g.file_path!)}
                        >
                          <Download className="mr-1 h-3 w-3" /> Download
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">Coming soon</span>
                      )}
                    </div>
                  ))
                ) : null}
              </div>
            )}

            <Button asChild className="w-full">
              <Link to="/"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
